export type TaskPromptContext = {
  projectId: number;
  projectName: string;
  taskNumber: number;
  bugherdUrl: string;
  title?: string;
  description?: string;
  originalCopyText?: string | null;
  pageUrl?: string;
  screenshotUrl?: string | null;
  isRecording?: boolean;
  attachments?: { name: string | null; url: string }[];
  selector?: string;
  browser?: {
    name?: string;
    os?: string;
    resolution?: string;
    viewport?: string;
    userAgent?: string;
  };
};

export type PromptTarget = 'cursor' | 'claude' | 'codex' | 'vscode';
export const MAX_PROMPT_URL_LENGTH = 10000;
export const PROMPT_APP_URLS: Record<PromptTarget, string> = {
  cursor: 'cursor://anysphere.cursor-deeplink/prompt',
  claude: 'claude://code/new',
  codex: 'codex://threads/new',
  vscode: 'vscode://GitHub.Copilot-Chat/chat'
};
// Copilot Chat decodes its query once, so these two fields are joined with %26.
export const PROMPT_QUERY: Record<PromptTarget, string> = {
  cursor: 'text',
  claude: 'q',
  codex: 'prompt',
  vscode: 'agent=agent%26prompt'
};

export function canUseTaskPrompt(role?: string): boolean {
  return [
    'owner',
    'admin',
    'manager',
    'member',
    'collaborator',
    'billing',
    'billing_manager'
  ].includes(role || '');
}

function escapeXml(value: string | number): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function field(name: string, value?: string | null): string {
  return value?.trim() ? `<${name}>${escapeXml(value)}</${name}>` : '';
}

function objectValue(value: unknown): Record<string, unknown> {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
}

// The board supplies JSON strings/snake_case, while the sidebar uses camelCase objects.
export function taskPromptTechnicalContext(
  browserInfo: unknown,
  selectorInfo: unknown,
  canViewSelector: boolean
): Pick<TaskPromptContext, 'browser' | 'selector'> {
  const browser = objectValue(browserInfo);
  const selector = objectValue(selectorInfo);
  const text = (value: unknown) =>
    typeof value === 'string' ? value : undefined;
  return {
    selector: canViewSelector ? text(selector.path) : undefined,
    browser: {
      name: text(browser.browser),
      os: text(browser.os),
      resolution: text(browser.resolution),
      viewport: text(browser.windowSize ?? browser.window_size),
      userAgent: text(browser.userAgent ?? browser.user_agent)
    }
  };
}

export function buildTaskPrompt(context: TaskPromptContext): string {
  const { projectId, taskNumber, projectName, browser } = context;
  const taskUrl = `${context.bugherdUrl.replace(
    /\/$/,
    ''
  )}/projects/${projectId}/tasks/${taskNumber}`;
  const details = [
    field('title', context.title),
    field('description', context.description),
    field('original-text', context.originalCopyText),
    context.originalCopyText?.trim()
      ? field('replacement-text', context.description)
      : '',
    field('url', taskUrl),
    field('page-url', context.pageUrl),
    field(
      context.isRecording ? 'recording' : 'screenshot',
      context.screenshotUrl
    ),
    ...(context.attachments || [])
      .filter(a => a.url)
      .map(
        a =>
          `<attachment name="${escapeXml(a.name || 'Attachment')}">${escapeXml(
            a.url
          )}</attachment>`
      ),
    field('selector', context.selector),
    field('browser', browser?.name),
    field('os', browser?.os),
    field('resolution', browser?.resolution),
    field('viewport', browser?.viewport),
    field('user-agent', browser?.userAgent)
  ].filter(Boolean);

  return [
    `Work on BugHerd task ${taskNumber} from project ${projectId}`,
    `<task project-id="${projectId}" task-id="${taskNumber}" project-name="${escapeXml(
      projectName
    )}">\n${details.join('\n')}\n</task>`
  ].join('\n\n');
}

export function taskPromptAppUrl(
  target: PromptTarget,
  prompt: string
): string | null {
  const url = `${PROMPT_APP_URLS[target]}?${
    PROMPT_QUERY[target]
  }=${encodeURIComponent(prompt)}`;
  return url.length <= MAX_PROMPT_URL_LENGTH ? url : null;
}
